/**
 * Content grid section component: parallax on block images.
 * Mounted on section with data-content-grid; uses data-content-grid-el="block" for blocks.
 * Parallax transform is applied to .sec-ContentGrid_MediaImage-desktop / -mobile.
 */
import Component from '../vendor/Component'

const PARALLAX_AMOUNT_MOBILE = 180
const PARALLAX_AMOUNT_DESKTOP = 260
const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)')
const SMALL_VIEWPORT_QUERY = window.matchMedia('(max-width: 767px)')

const parallaxBlocks = []
let scrollTicking = false
let scrollListenerAdded = false

function getElementOffsetTop (element) {
  const rect = element.getBoundingClientRect()
  return rect.top + (window.scrollY ?? window.pageYOffset)
}

function applyParallaxTransforms () {
  const scrollY = window.scrollY ?? window.pageYOffset
  const viewportHeight = window.innerHeight
  const amount = SMALL_VIEWPORT_QUERY.matches ? PARALLAX_AMOUNT_MOBILE : PARALLAX_AMOUNT_DESKTOP

  parallaxBlocks.forEach(({ blockElement, targets }) => {
    const blockHeight = blockElement.getBoundingClientRect().height || blockElement.offsetHeight
    if (!blockHeight) return

    const blockTop = getElementOffsetTop(blockElement)
    const start = blockTop - viewportHeight
    const end = blockTop + blockHeight

    let translateY
    if (scrollY <= start) {
      translateY = -amount / 2
    } else if (scrollY >= end) {
      translateY = amount / 2
    } else {
      const progress = (scrollY - start) / (end - start)
      translateY = amount * progress - amount / 2
    }

    const value = `translate3d(0, ${Math.round(translateY)}px, 0)`
    targets.forEach((el) => { el.style.transform = value })
  })
}

function onScroll () {
  if (!scrollTicking) {
    scrollTicking = true
    requestAnimationFrame(() => {
      scrollTicking = false
      applyParallaxTransforms()
    })
  }
}

function ensureScrollListener () {
  if (parallaxBlocks.length === 0 || scrollListenerAdded) return
  scrollListenerAdded = true
  window.addEventListener('scroll', onScroll, { passive: true })
}

function removeScrollListener () {
  if (parallaxBlocks.length > 0) return
  scrollListenerAdded = false
  window.removeEventListener('scroll', onScroll)
}

const BLOCK_SELECTOR = '[data-content-grid-el="block"]'
const WRAPPER_SELECTOR = '[data-content-grid-parallax-wrapper]'
const DESKTOP_IMAGE_SELECTOR = '.sec-ContentGrid_MediaImage-desktop'
const MOBILE_IMAGE_SELECTOR = '.sec-ContentGrid_MediaImage-mobile'

function addParallaxBlocksForSection (sectionEl) {
  if (REDUCED_MOTION_QUERY.matches) return
  const blockElements = sectionEl.querySelectorAll(BLOCK_SELECTOR)
  blockElements.forEach((blockEl) => {
    const wrapper = blockEl.querySelector(WRAPPER_SELECTOR)
    if (!wrapper || wrapper._contentGridParallaxInit) return

    const desktopImage = blockEl.querySelector(DESKTOP_IMAGE_SELECTOR)
    const mobileImage = blockEl.querySelector(MOBILE_IMAGE_SELECTOR)
    const targets = [desktopImage, mobileImage].filter(Boolean)
    if (targets.length === 0) return

    wrapper._contentGridParallaxInit = true
    parallaxBlocks.push({ blockElement: blockEl, targets })
  })
  if (parallaxBlocks.length > 0) {
    ensureScrollListener()
    requestAnimationFrame(() => {
      requestAnimationFrame(applyParallaxTransforms)
    })
  }
}

function initContentGridParallax () {
  document.querySelectorAll('[data-content-grid]').forEach(addParallaxBlocksForSection)
}

function destroyContentGridParallax (container) {
  if (!container) return
  const toRemove = parallaxBlocks.filter((entry) => container.contains(entry.blockElement))
  toRemove.forEach((entry) => {
    const wrapper = entry.blockElement.querySelector(WRAPPER_SELECTOR)
    if (wrapper) wrapper._contentGridParallaxInit = false
    entry.targets.forEach((el) => { el.style.transform = '' })
  })
  toRemove.forEach((entry) => {
    const i = parallaxBlocks.indexOf(entry)
    if (i !== -1) parallaxBlocks.splice(i, 1)
  })
  removeScrollListener()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initContentGridParallax)
} else {
  initContentGridParallax()
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target
  if (!section.hasAttribute('data-content-grid') && !section.classList.contains('sec-ContentGrid')) return
  addParallaxBlocksForSection(section)
})

document.addEventListener('shopify:section:unload', (event) => {
  destroyContentGridParallax(event.target)
})

export default class ContentGrid extends Component {
  constructor(elem, theme, options, ctx) {
    super(elem, theme, options, ctx)
    this.parallaxEntries = []
  }

  async mount () {
    addParallaxBlocksForSection(this._elem)

    this.parallaxEntries = []
  }

  unmount () {
    destroyContentGridParallax(this._elem)
    this.parallaxEntries = []
  }
}
